import { ReactNode } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
// ISDP CUSTOM: clean copy-link control on headings (replaces the ugly ¶ permalink)
import {
  TechDocsResponsiveStyles,
  TechDocsCopyPermalink,
  TechDocsDownloadButton,
} from '../techdocs/techDocsAddons';
import { TechDocsLanguageProxy } from '../techdocs/TechDocsLanguageProxy';


// ISDP CUSTOM: Azure DevOps plugin integration — adds CI/CD, PRs, Git Tags, README tabs
import {
  EntityAzurePipelinesContent,
  EntityAzurePullRequestsContent,
  EntityAzureGitTagsContent,
  EntityAzureReadmeCard,
  isAzureDevOpsAvailable,
  isAzurePipelinesAvailable,
} from '@backstage-community/plugin-azure-devops';

// ISDP CUSTOM: Custom catalog cards — endpoint consumption, reverse-lookup consumers, system docs
// ISDP CUSTOM: Guard for TechDocs tab — only show when annotation is present
const isTechdocsAvailable = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['backstage.io/techdocs-ref']);

import { SystemDocsCard } from './SystemDocsCard';
import { ApiEndpointsCard } from './ApiEndpointsCard';
import { ApiConsumersEndpointCard } from './ApiConsumersEndpointCard';
import {
  EntityAboutRow,
  EntityLinksSection,
  IfRelation,
} from './EntityOverviewCards';
import { EntityDetailsHeader } from './EntityDetailsHeader';
import { isPreview } from './entityHelpers';
import { DocsIconNudge } from './DocsIconNudge';
import './EntityPage.css';

// ISDP CUSTOM: unmissable PREVIEW/DRAFT banner on the entity detail page. A
// preview entity (spec.lifecycle=preview) is an unpublished draft — viewable by
// internal users via a direct link or the preview toggle, but kept out of search,
// the Curator, the MCP face and the normal listings. This banner makes sure it can
// never be read as an official, published entry. Renders nothing for normal entities.
const EntityPreviewWarning = () => {
  const { entity } = useEntity();
  if (!isPreview(entity)) return null;
  return (
    <div className="devhub-entity-grid__item--md12">
      <div role="alert" className="devhub-entity-preview-warning">
        <odx-icon name="core::warning" />
        <div className="devhub-entity-preview-warning__body">
          <span>
            <strong>Preview — unpublished draft.</strong> This entry is not official
            and may change or be removed. It is hidden from search, the assistant and
            the normal catalog/docs listings.
          </span>
          <DocsIconNudge entity={entity} />
        </div>
      </div>
    </div>
  );
};

const techdocsContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isTechdocsAvailable}>
      {/* PREVIEW/DRAFT warning above the embedded reader — shown regardless of the
          doc's build state, since the reader is also reachable by direct link. */}
      <EntityPreviewWarning />
      <EntityTechdocsContent>
        <TechDocsAddons>
          <ReportIssue />
          <TechDocsResponsiveStyles />
          <TechDocsCopyPermalink />
          <TechDocsDownloadButton />
          {/* No TechDocsPreviewBanner here — the full-width EntityPreviewWarning
              above already covers the entity Docs tab; the addon is only for the
              standalone /docs reader (App.tsx), which has no EntityPreviewWarning. */}
          <TechDocsLanguageProxy />
          {/* Match App.tsx so Mermaid diagrams render in the entity Docs tab too, not just /docs/*. */}
          <Mermaid config={{ theme: 'neutral', themeVariables: { primaryColor: '#e6f0f6' } }} />
        </TechDocsAddons>
      </EntityTechdocsContent>
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No documentation available"
        missing="info"
        description="Add the backstage.io/techdocs-ref annotation to your entity's catalog-info.yaml to enable TechDocs."
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

// ISDP CUSTOM: CI/CD tab — shows Azure DevOps pipelines when annotation is present.
// The pipeline list is rendered by @backstage-community/plugin-azure-devops's
// EntityAzurePipelinesContent (a Backstage BUI resizable table — we can't restructure
// its columns without forking the plugin). We wrap it in .devhub-cicd and apply
// scoped CSS in EntityPage.css so the table reads as a clean, page-consistent table:
// no awkward mid-value wrapping in the Build/Source columns, aligned padding, etc.
const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isAzurePipelinesAvailable}>
      <div className="devhub-cicd">
        <EntityAzurePipelinesContent defaultLimit={25} />
      </div>
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <a
            className="devhub-entity-btn"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read more
          </a>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntityPreviewWarning />

    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <div className="devhub-entity-grid__item--md12">
          <EntityOrphanWarning />
        </div>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* ISDP CUSTOM: The relation-not-found warning (<EntityRelationWarning> /
        hasRelationWarnings) is intentionally suppressed. Our owner group
        `group:default/draeger` (Group kind) is deliberately excluded from the
        catalog view, so every entity's owner relation looks "dangling" and would
        fire this orange banner on every page — pure noise for a demo. The orphan
        warning and processing-errors panel below remain, since those are real. */}

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <div className="devhub-entity-grid__item--md12">
          <EntityProcessingErrorsPanel />
        </div>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <div className="devhub-entity-grid">
    {entityWarningContent}
    {/* ISDP CUSTOM: title + star / "View source" / "Last updated" row. */}
    <EntityDetailsHeader />
    {/* ISDP CUSTOM: About (full width when there's no relations graph beside it) plus
        the relations graph when the entity has relations worth drawing. */}
    <EntityAboutRow />

    {/* ISDP CUSTOM: Azure DevOps README card on entity overview */}
    <EntitySwitch>
      <EntitySwitch.Case if={isAzureDevOpsAvailable}>
        <div className="devhub-entity-grid__item--md6">
          <EntityAzureReadmeCard maxHeight={350} />
        </div>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntityLinksSection />
    {/* ISDP CUSTOM (stakeholder feedback): "Has subcomponents" relations card hidden
        on the Details/Overview tab. Gated off (not deleted) so it's trivial to restore —
        flip the `false &&` to a condition (or `true`). */}
    {false && (
      <div className="devhub-entity-grid__item--md8">
        <EntityHasSubcomponentsCard variant="gridItem" />
      </div>
    )}
  </div>
);

const dependenciesContent = (
  <div className="devhub-entity-grid">
    <div className="devhub-entity-grid__item--md6">
      <EntityDependsOnComponentsCard variant="gridItem" />
    </div>
    <div className="devhub-entity-grid__item--md6">
      <EntityDependsOnResourcesCard variant="gridItem" />
    </div>
  </div>
);

const apiTabContent = (
  <div className="devhub-entity-grid">
    <div className="devhub-entity-grid__item--md6">
      <EntityProvidedApisCard />
    </div>
    <div className="devhub-entity-grid__item--md6">
      <EntityConsumedApisCard />
    </div>
    <div className="devhub-entity-grid__item--md12">
      <ApiEndpointsCard />
    </div>
  </div>
);

// ISDP CUSTOM: Build a component entity layout. "Details" is always the landing tab
// (stakeholder feedback): clicking a catalog card or a Landscape tile is a request to
// see the entry itself. Arriving "from docs" never lands here — the docs listings, the
// search results and the Curator all link to `docsPath()` → /docs/:ns/:kind/:name,
// which App.tsx routes to the standalone TechDocsReaderPage, a different page from
// this one. The Docs tab below stays, at /docs, for readers who come via the tabs.
//
// IMPORTANT: the index/landing route uses path="" (empty), NOT path="/". Backstage's
// RoutedTabs turns each route path into a match pattern of `${path}/*`. With path="/"
// that becomes the malformed "//*", which matches the bare entity URL but NOT deeper
// sub-paths. The Docs tab needs its own catch-all for that reason too: doc chapter
// links push sub-paths like ".../docs/architecture/overview", and the "/docs" route
// pattern ("/docs/*") keeps the Docs tab selected across all of them.
const buildComponentEntityLayout = ({
  middleRoutes,
}: {
  middleRoutes?: ReactNode;
}) => (
  <EntityLayout>
    <EntityLayout.Route path="" title="Details">
      {overviewContent}
    </EntityLayout.Route>

    {/* ISDP CUSTOM (stakeholder feedback): the CI/CD tab only appears when the entity
        actually has Azure Pipelines wired (isAzurePipelinesAvailable = the
        dev.azure.com/project + build-definition annotations are present). Previously
        the tab always rendered and showed an empty "No CI/CD available" state for
        entities with no pipelines. Gating the Route with `if=` hides the tab entirely
        for those. To restore the always-on behaviour, drop the `if` prop. */}
    <EntityLayout.Route
      path="/ci-cd"
      title="CI/CD"
      if={isAzurePipelinesAvailable}
    >
      {cicdContent}
    </EntityLayout.Route>

    {middleRoutes}

    {/* ISDP CUSTOM (stakeholder feedback): Dependencies tab hidden. Kept in place but
        gated off with `if={() => false}` so the tab never shows (and dependenciesContent
        stays referenced) — restore by removing the `if` prop. */}
    <EntityLayout.Route path="/dependencies" title="Dependencies" if={() => false}>
      {dependenciesContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const serviceMiddleRoutes = (
  <>

    {/* ISDP CUSTOM: Azure DevOps Pull Requests tab */}
    <EntityLayout.Route
      path="/pull-requests"
      title="Pull Requests"
      if={isAzureDevOpsAvailable}
    >
      <EntityAzurePullRequestsContent />
    </EntityLayout.Route>

    {/* ISDP CUSTOM: Azure DevOps Git Tags tab */}
    <EntityLayout.Route
      path="/git-tags"
      title="Git Tags"
      if={isAzureDevOpsAvailable}
    >
      <EntityAzureGitTagsContent />
    </EntityLayout.Route>

    {/* ISDP CUSTOM: API tab with custom ApiEndpointsCard showing isdp.io/api-endpoints annotation */}
    <EntityLayout.Route path="/api" title="API">
      {apiTabContent}
    </EntityLayout.Route>
  </>
);

// Websites have no extra middle tabs of their own (the Kubernetes tab was removed
// with the unused plugin); the shared layout supplies the rest.
const websiteMiddleRoutes = <></>;

const serviceEntityPage = buildComponentEntityLayout({
  middleRoutes: serviceMiddleRoutes,
});

const websiteEntityPage = buildComponentEntityLayout({
  middleRoutes: websiteMiddleRoutes,
});

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * The CSS grid layout collapses to single-column at smaller breakpoints.
 */

const defaultEntityPage = buildComponentEntityLayout({});

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Details">
      <div className="devhub-entity-grid">
        {entityWarningContent}
        <EntityDetailsHeader />
        <EntityAboutRow />
        <EntityLinksSection />
        <div className="devhub-entity-grid--nested">
          <IfRelation
            type={RELATION_API_PROVIDED_BY}
            className="devhub-entity-grid__item--md6"
          >
            <EntityProvidingComponentsCard />
          </IfRelation>
          <IfRelation
            type={RELATION_API_CONSUMED_BY}
            className="devhub-entity-grid__item--md6"
          >
            <EntityConsumingComponentsCard />
          </IfRelation>
        </div>
        {/* ISDP CUSTOM: Reverse-lookup card showing which components consume this API's endpoints */}
        <div className="devhub-entity-grid__item--md12">
          <ApiConsumersEndpointCard />
        </div>
      </div>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <div className="devhub-entity-grid">
        <div className="devhub-entity-grid__item--md12">
          <EntityApiDefinitionCard />
        </div>
      </div>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Details">
      <div className="devhub-entity-grid">
        {entityWarningContent}
        <div className="devhub-entity-grid__item--md6 devhub-entity-grid__item--fill">
          <EntityUserProfileCard />
        </div>
        <div className="devhub-entity-grid__item--md6 devhub-entity-grid__item--fill">
          <EntityOwnershipCard />
        </div>
      </div>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Details">
      <div className="devhub-entity-grid">
        {entityWarningContent}
        <div className="devhub-entity-grid__item--md6 devhub-entity-grid__item--fill">
          <EntityGroupProfileCard />
        </div>
        <div className="devhub-entity-grid__item--md6 devhub-entity-grid__item--fill">
          <EntityOwnershipCard />
        </div>
        <div className="devhub-entity-grid__item--md6">
          <EntityMembersListCard />
        </div>
        <EntityLinksSection />
      </div>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Details">
      <div className="devhub-entity-grid">
        {entityWarningContent}
        <EntityDetailsHeader />
        <EntityAboutRow />
        <EntityLinksSection />
        <IfRelation
          type={RELATION_HAS_PART}
          kind="component"
          className="devhub-entity-grid__item--md8"
        >
          <EntityHasComponentsCard variant="gridItem" />
        </IfRelation>
        <IfRelation
          type={RELATION_HAS_PART}
          kind="api"
          className="devhub-entity-grid__item--md6"
        >
          <EntityHasApisCard variant="gridItem" />
        </IfRelation>
        <IfRelation
          type={RELATION_HAS_PART}
          kind="resource"
          className="devhub-entity-grid__item--md6"
        >
          <EntityHasResourcesCard variant="gridItem" />
        </IfRelation>
      </div>
    </EntityLayout.Route>
    {/* ISDP CUSTOM: System Documentation tab — custom SystemDocsCard that aggregates TechDocs from child components */}
    <EntityLayout.Route path="/documentation" title="Documentation">
      <SystemDocsCard />
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Details">
      <div className="devhub-entity-grid">
        {entityWarningContent}
        <EntityDetailsHeader />
        <EntityAboutRow />
        <IfRelation
          type={RELATION_HAS_PART}
          kind="system"
          className="devhub-entity-grid__item--md6"
        >
          <EntityHasSystemsCard variant="gridItem" />
        </IfRelation>
      </div>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
